import { IonCard, IonSkeletonText } from '@ionic/react'
import dayjs from 'dayjs'
import React, { useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useConsumptionReports } from '../../contexts/hooks/useConsumptionReports'
import { ModalContext } from '../../contexts/ModalContext'
import { ObjectContext } from '../../contexts/objectContext/ObjectContext'
import i18n from '../../i18n'
import PageWrapper from '../../wrappers/pageWrapper/PageWrapper'
import YearGroup from './components/YearGroup'
import { groupReportsByYear } from './utils'

const MonthlyReportsList: React.FC = () => {
	const object = useContext(ObjectContext)
	const { insertModal, setActiveReportId } = useContext(ModalContext)

	const { t } = useTranslation('pages_monthlyReportsList')
	dayjs.locale(i18n.language)

	const reports = useConsumptionReports(object.id)

	const currentYear = dayjs().format('YYYY')

	// Only stores explicit overrides from user clicks; years default to
	// collapsed unless they are the current year.
	const [yearOverrides, setYearOverrides] = useState<Map<string, boolean>>(new Map())

	const isYearCollapsed = (year: string) => yearOverrides.get(year) ?? year !== currentYear

	const toggleYear = (year: string) => {
		setYearOverrides(prev => {
			const next = new Map(prev)
			next.set(year, !isYearCollapsed(year))
			return next
		})
	}

	const reportsByYear = useMemo(() => groupReportsByYear(reports.data ?? []), [reports.data])

	const openReport = (reportId: number) => {
		insertModal('monthlyReportDetail')
		setActiveReportId(reportId)
	}

	return (
		<PageWrapper page='monthlyReports'>
			{reports.isLoading ? (
				<div className='flex flex-col gap-2'>
					{[1].map(item => (
						<IonCard
							className='flex flex-col shadow-card bg-white dark:bg-brand-secondary-200-dark'
							key={item}
						>
							<div className='flex items-center'>
								<div className='flex flex-col text-center gap-4'>
									<IonSkeletonText animated className='w-10 h-10' />
									<IonSkeletonText animated className='w-16 h-5' />
								</div>
								<div className='flex flex-col justify-center ml-[1.4rem]'>
									<IonSkeletonText animated className='w-24 h-4' />
									<IonSkeletonText animated className='w-20 h-5 mt-1' />
								</div>

								<div className='grow' />

								<IonSkeletonText animated className='w-6 h-6 rounded-full' />
							</div>

							<div className='flex gap-6 mt-[0.9rem] pt-[0.9rem] border-t border-[#e5e5e5] dark:border-brand-primary-200-dark'>
								<IonSkeletonText animated className='w-24 h-4' />
								<IonSkeletonText animated className='w-24 h-4' />
							</div>
						</IonCard>
					))}
				</div>
			) : reports.data && reports.data.length > 0 ? (
				<div className='flex flex-col gap-3'>
					{reportsByYear.map(([year, yearReports]) => (
						<YearGroup
							key={year}
							year={year}
							reports={yearReports}
							allReports={reports.data ?? []}
							isCollapsed={isYearCollapsed(year)}
							onToggle={() => toggleYear(year)}
							onSelectReport={openReport}
						/>
					))}
				</div>
			) : (
				<IonCard className='bg-white dark:bg-brand-secondary-200-dark text-text-dark dark:text-white p-4'>
					{object.id === 0 ? (
						<h1>{t('userHasNoObjects')}</h1>
					) : (
						<>
							<h1>{t('eiRaportteja')}</h1>
							<p>{t('luodaan')}</p>
						</>
					)}
				</IonCard>
			)}
		</PageWrapper>
	)
}

export default MonthlyReportsList
